import { Download, ImageOff, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { formatSize, getExtension, getKind, KIND_ICONS } from '../ImagePreview';
import { downloadNoteAttachment, getNoteAttachmentUrl, isImageAttachment } from '../noteAttachments';

// A note's files are private: each view asks for a short-lived link. A note
// that is still saving shows its local preview instead.
const useAttachmentUrl = (attachment) => {
  const { key, name, preview } = attachment;
  const [state, setState] = useState({ url: preview || null, failed: false });

  useEffect(() => {
    if (preview) {
      setState({ url: preview, failed: false });
      return undefined;
    }
    if (!key) {
      setState({ url: null, failed: true });
      return undefined;
    }
    let active = true;
    getNoteAttachmentUrl(key, { name })
      .then((url) => active && setState({ url, failed: false }))
      .catch(() => active && setState({ url: null, failed: true }));
    return () => {
      active = false;
    };
  }, [key, name, preview]);

  return state;
};

const NoteImage = ({ attachment, single, onOpen }) => {
  const { url, failed } = useAttachmentUrl(attachment);
  const [broken, setBroken] = useState(false);
  const name = attachment.name || 'Photo';
  const frame = cn(
    'relative flex items-center justify-center overflow-hidden rounded-xl bg-yellow-200/40 dark:bg-yellow-950/60',
    single ? 'h-48 w-64 max-w-full max-md:h-40 max-md:w-56' : 'size-28 max-md:size-24',
  );

  if (failed || broken) {
    return (
      <div className={cn(frame, 'flex-col gap-1 text-xs text-muted-foreground')} title={name}>
        <ImageOff className="size-5" aria-hidden />
        Photo unavailable
      </div>
    );
  }

  if (!url) {
    return (
      <div className={frame}>
        <Loader2 className="size-5 animate-spin text-muted-foreground" aria-label={`Loading ${name}`} />
      </div>
    );
  }

  return (
    <button
      type="button"
      className={cn(frame, 'cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500')}
      aria-label={`View ${name}`}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(attachment, url);
      }}
    >
      <img src={url} alt={name} className="size-full object-cover" onError={() => setBroken(true)} />
    </button>
  );
};

const NoteFile = ({ attachment }) => {
  const [downloading, setDownloading] = useState(false);
  const Icon = KIND_ICONS[getKind(attachment)];
  const name = attachment.name || 'Attachment';
  const details = [getExtension(name).toUpperCase(), formatSize(attachment.size)].filter(Boolean).join(' · ');
  // A note that is still saving has nothing to download yet
  const ready = Boolean(attachment.key) && !attachment.preview;

  const download = async (event) => {
    event.stopPropagation();
    if (!ready || downloading) return;
    setDownloading(true);
    try {
      await downloadNoteAttachment(attachment);
    } catch {
      toast.error(`${name} is unavailable.`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={download}
      disabled={!ready}
      aria-label={`Download ${name}`}
      className="flex w-64 max-w-full items-center gap-2.5 rounded-xl border border-yellow-200/80 bg-background/70 p-1.5 pr-2.5 text-left transition-colors hover:bg-background disabled:cursor-default dark:border-yellow-800/60 max-md:min-h-11"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-yellow-200/50 text-yellow-800 dark:bg-yellow-900/60 dark:text-yellow-300">
        <Icon className="size-4" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium leading-tight" title={name}>
          {name}
        </span>
        {details && <span className="block truncate text-xs text-muted-foreground">{details}</span>}
      </span>
      {ready &&
        (downloading ? (
          <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" aria-hidden />
        ) : (
          <Download className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        ))}
    </button>
  );
};

const NoteAttachments = ({ attachments, showImageLightbox }) => {
  if (!attachments?.length) return null;

  const images = attachments.filter(isImageAttachment);
  const files = attachments.filter((attachment) => !isImageAttachment(attachment));

  // Ask again when opening: a link from earlier may have expired.
  const openImage = async (attachment, url) => {
    if (!attachment.key || attachment.preview) return showImageLightbox?.(url);
    try {
      showImageLightbox?.(await getNoteAttachmentUrl(attachment.key, { name: attachment.name }));
    } catch {
      toast.error('This photo is unavailable.');
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {images.map((attachment) => (
            <NoteImage
              key={attachment.key || attachment.path}
              attachment={attachment}
              single={images.length === 1}
              onOpen={openImage}
            />
          ))}
        </div>
      )}
      {files.map((attachment) => (
        <NoteFile key={attachment.key || attachment.path} attachment={attachment} />
      ))}
    </div>
  );
};

export default NoteAttachments;
